import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './accounting.css';

const ViewBank = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bank, setBank] = useState(null);

  useEffect(() => {
    fetchBank();
  }, [id]);

  const fetchBank = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/accounting/banks/${id}`);
      setBank(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ في تحميل بيانات البنك');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await axios.patch(`/api/accounting/banks/${id}/toggle-status`);
      fetchBank();
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ في تغيير الحالة');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('هل أنت متأكد من حذف هذا البنك؟')) {
      try {
        await axios.delete(`/api/accounting/banks/${id}`);
        navigate('/banks');
      } catch (err) {
        setError(err.response?.data?.message || 'حدث خطأ في حذف البنك');
      }
    }
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
        {isActive ? t('accounting.banks.status.active') : t('accounting.banks.status.inactive')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bank-view-container">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !bank) {
    return (
      <div className="bank-view-container">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bank-view-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h2>{t('accounting.banks.viewBank')}</h2>
          <p>عرض بيانات البنك: {bank?.bank_code}</p>
        </div>
        <div className="header-actions">
          <button 
            className="back-btn"
            onClick={() => navigate('/banks')}
          >
            <i className="fas fa-arrow-right"></i>
            {t('accounting.banks.actions.back')}
          </button>
          <button 
            className="btn-edit"
            onClick={() => navigate(`/banks/edit/${id}`)}
          >
            <i className="fas fa-edit"></i>
            {t('accounting.banks.actions.edit')}
          </button>
          <button 
            className="btn-post"
            onClick={handleToggleStatus}
          >
            <i className={`fas fa-${bank?.is_active ? 'pause' : 'play'}`}></i>
            {t('accounting.banks.actions.toggleStatus')}
          </button>
          <button 
            className="btn-delete"
            onClick={handleDelete}
          >
            <i className="fas fa-trash"></i>
            {t('accounting.banks.actions.delete')}
          </button>
        </div>
      </div>

      <div className="form-container">
        {/* Help Panels */}
        <div className="help-panels">
          <div className="help-panel">
            <h4>{t('accounting.banks.help.title')}</h4>
            <p>{t('accounting.banks.help.description')}</p>
            <ul>
              <li>{t('accounting.banks.help.bankInfo')}</li>
              <li>{t('accounting.banks.help.accountInfo')}</li>
              <li>{t('accounting.banks.help.statusInfo')}</li>
            </ul>
          </div>
          <div className="guide-panel">
            <h4>معلومات البنك</h4>
            <ul>
              <li>رمز البنك: {bank?.bank_code}</li>
              <li>الحالة: {getStatusBadge(bank?.is_active)}</li>
              <li>تاريخ الإنشاء: {new Date(bank?.created_at).toLocaleDateString('ar-SA')}</li>
              <li>آخر تحديث: {new Date(bank?.updated_at).toLocaleDateString('ar-SA')}</li>
            </ul>
          </div>
        </div>

        {/* Bank Details Section */}
        <div className="form-section">
          <div className="form-card">
            <div className="bank-details">
              <div className="detail-section">
                <h4>البيانات الأساسية</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-label">{t('accounting.banks.bankName')}</div>
                    <div className="detail-value">{bank?.bank_name}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">{t('accounting.banks.bankCode')}</div>
                    <div className="detail-value">{bank?.bank_code}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">{t('accounting.banks.swiftCode')}</div>
                    <div className="detail-value">{bank?.swift_code || '-'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">{t('accounting.banks.ibanPrefix')}</div>
                    <div className="detail-value">{bank?.iban_prefix || '-'}</div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>معلومات التواصل</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-label">{t('accounting.banks.contactPerson')}</div>
                    <div className="detail-value">{bank?.contact_person || '-'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">{t('accounting.banks.phone')}</div>
                    <div className="detail-value">{bank?.phone || '-'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">{t('accounting.banks.email')}</div>
                    <div className="detail-value">
                      {bank?.email ? (
                        <a href={`mailto:${bank.email}`}>{bank.email}</a>
                      ) : '-'}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">{t('accounting.banks.website')}</div>
                    <div className="detail-value">
                      {bank?.website ? (
                        <a href={bank.website} target="_blank" rel="noopener noreferrer">
                          {bank.website}
                        </a>
                      ) : '-'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>{t('accounting.banks.address')}</h4>
                <div className="detail-item">
                  <div className="detail-value">
                    {bank?.address || 'لا يوجد عنوان محدد'}
                  </div>
                </div>
              </div>

              {bank?.logo && (
                <div className="detail-section">
                  <h4>{t('accounting.banks.logo')}</h4>
                  <div className="detail-item">
                    <div className="detail-value">
                      <img 
                        src={`/storage/${bank.logo}`} 
                        alt="شعار البنك" 
                        className="bank-logo"
                        style={{ maxWidth: '200px', maxHeight: '100px' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => navigate('/banks')}
              >
                {t('accounting.banks.actions.back')}
              </button>
              <button
                type="button"
                className="btn-edit"
                onClick={() => navigate(`/banks/edit/${id}`)}
              >
                {t('accounting.banks.actions.edit')}
              </button>
              <button
                type="button"
                className="btn-post"
                onClick={handleToggleStatus}
              >
                {t('accounting.banks.actions.toggleStatus')}
              </button>
              <button
                type="button"
                className="btn-delete"
                onClick={handleDelete}
              >
                {t('accounting.banks.actions.delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBank;
